import type { AnchorProvider, Program } from "@coral-xyz/anchor";
import { TOKEN_PROGRAM_ID } from "@solana/spl-token";
import type { Connection, TransactionInstruction } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import type { StepRewardPool } from "@stepfinance/farms";

import { TokenChangeNotificationDisplay } from "../../../components/Notifications/TokenChangeNotificationDisplay";
import { confirmOptions } from "../../../constants/transaction";
import type { ConnectedWallet } from "../../../hooks/wallet/useConnectedWallet";
import type { StepRewardPoolIDL } from "../../../models/step/farms/gen/step_reward_pool";
import { sendTransaction } from "../../../utils/transactions";
import { findOrCreateAccountByMint } from "../..";
import { getStepRewardProgram, getStepRewardProvider } from "./anchor";

export const buildClaimInstructions = async (
  provider: AnchorProvider,
  program: Program<StepRewardPoolIDL>,
  connection: Connection,
  pool: StepRewardPool,
  poolPublicKey: PublicKey,
  poolSigner: PublicKey,
  userAccountPublicKey: PublicKey,
) => {
  const instructions: TransactionInstruction[] = [];
  const rewardAAccount = await findOrCreateAccountByMint(
    connection,
    provider.wallet.publicKey,
    provider.wallet.publicKey,
    instructions,
    pool.rewardAMint,
  );
  let rewardBAccount: PublicKey;
  if (pool.rewardAMint.equals(pool.rewardBMint)) {
    rewardBAccount = rewardAAccount;
  } else {
    rewardBAccount = await findOrCreateAccountByMint(
      connection,
      provider.wallet.publicKey,
      provider.wallet.publicKey,
      instructions,
      pool.rewardBMint,
    );
  }
  instructions.push(
    program.instruction.claim({
      accounts: {
        // Stake instance.
        pool: poolPublicKey,
        stakingVault: pool.stakingVault,
        rewardAVault: pool.rewardAVault,
        rewardBVault: pool.rewardBVault,
        // User.
        user: userAccountPublicKey,
        owner: provider.wallet.publicKey,
        rewardAAccount,
        rewardBAccount,
        // Program signers.
        poolSigner,
        // Misc.
        tokenProgram: TOKEN_PROGRAM_ID,
      },
    }),
  );
  return instructions;
};

export const claimRewards = async (
  connection: Connection,
  wallet: ConnectedWallet,
  poolPublicKey: PublicKey,
  userAccountPublicKey: PublicKey,
  claimableRewards: number,
  rewardMint: PublicKey,
  claimableRewardsB?: number,
  rewardMintB?: PublicKey,
) => {
  const provider = getStepRewardProvider(connection, wallet);
  const program = getStepRewardProgram(provider);
  const pool = await program.account.pool.fetch(poolPublicKey);
  const [poolSigner] = await PublicKey.findProgramAddress([poolPublicKey.toBuffer()], program.programId);
  const instructions = await buildClaimInstructions(
    provider,
    program,
    connection,
    pool,
    poolPublicKey,
    poolSigner,
    userAccountPublicKey,
  );

  return sendTransaction(connection, wallet, instructions, [], true, confirmOptions, true, {
    inProgress: {
      message: "You are claiming rewards",
      description: "Confirmation is in progress.",
    },
    success: {
      message: "You claimed rewards",
      description: (
        <>
          <span>You claimed:</span>
          <TokenChangeNotificationDisplay amount={claimableRewards} mintAddress={rewardMint} type="increase" />
          {claimableRewardsB && rewardMintB && (
            <TokenChangeNotificationDisplay amount={claimableRewardsB} mintAddress={rewardMintB} type="increase" />
          )}
        </>
      ),
    },
  });
};
